import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { post, withPath } from '@app/api/typed';
import type { AgentAskResult } from './ChatModel';

// O elenco (`useLabPeople`) e as identidades com contrato (`useIdentities(true)`)
// são as **mesmas** consultas da ConversePage — importadas de lá pela tela, não
// reescritas aqui.

/** Um envio: a pergunta, a quem, e por quem (exatamente um de person/claims). */
export interface AskSend {
  agent: string;
  question: string;
  person?: string;
  claims?: Record<string, unknown>;
  /** The id of the question (aim-6ph.10), created by the screen. */
  conversationId?: string;
}

/**
 * Teto da pergunta, em ms. O agente fala com o modelo (até `LLM_MAX_ROUNDS`
 * rodadas de `LLM_TIMEOUT_S`, §1) e depois chama a tool: o padrão do cliente
 * (20 s) não cobre uma pergunta que delega. É decisão de produto, como nas
 * ordens longas — daí o campo próprio.
 *
 * 59 s porque a cadeia por trás cabe abaixo das duas bordas de rede, que cortam
 * em 60 s (aim-988, decisão do mrolim, 12/10/2026): 45 s no agente → 50 s no
 * executor → 53 s no cliente do backend (`aig.AskTimeout`) → 58 s na requisição
 * HTTP (`askRequestCeiling`). Cada camada fica acima da de dentro, para que quem
 * responda ao usuário seja sempre a mais interna, com o código dela. Se mesmo
 * assim a espera passar de um minuto, a tela diz o motivo (`isEdgeCut`).
 */
export const ASK_TIMEOUT_MS = 59_000;

/**
 * A espera passou de um minuto (aim-988): a borda de rede cortou (um 504 sem o envelope do console, a página HTML do
 * proxy ou da entrada do cluster) ou o próprio navegador desistiu (`tempo_esgotado`). Os dois têm a mesma causa para
 * quem pergunta, e a tela diz o motivo em vez do erro genérico. Um 504 COM envelope é do console, com o código dele, e
 * não entra aqui.
 */
export function isEdgeCut(error: ApiError | null | undefined): boolean {
  if (!error) return false;
  if (error.code === 'tempo_esgotado') return true;
  return error.code === 'erro_desconhecido' && error.status === 504;
}

/**
 * Pergunta ao agente. `useMutation`, como `useTriggerCall`, pela razão mais
 * forte que existe: **a pergunta tem efeito** — o modelo pode chamar uma tool
 * no MCP do cliente, e uma query que revalidasse sozinha perguntaria de novo.
 */
export function useAsk(): UseMutationResult<AgentAskResult, ApiError, AskSend> {
  return useMutation<AgentAskResult, ApiError, AskSend>({
    mutationFn: (send) =>
      post(withPath('/api/v1/agents/{slug}/ask', { slug: send.agent }) as '/api/v1/agents/{slug}/ask', {
        body: {
          question: send.question,
          ...(send.person ? { person: send.person } : {}),
          ...(send.claims ? { claims: send.claims } : {}),
          ...(send.conversationId ? { conversation_id: send.conversationId } : {}),
        },
        timeoutMs: ASK_TIMEOUT_MS,
      }),
  });
}
